import React, { useState, useEffect } from 'react';
import { firebaseAppointmentService, Appointment, ProviderAvailability, AppointmentModality, AppointmentTypeKey } from '../../../services/firebase/FirebaseAppointmentService';
import { FirebasePatientService, Patient } from '../../../services/firebase/FirebasePatientService';
import Calendar from '../../Calendar';
import TimeBlockView from '../../TimeBlockView';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { hasAnyRole } from '../../../utils/permissions';
import { RoleUser } from '../../../utils/permissions';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';
import VoiceInputButton from '../../VoiceInputButton';
import EmailNotificationService from '../../../services/EmailNotificationService';
import NotificationTriggerService from '../../../services/NotificationTriggerService';
import { FirebaseCoupleService, CoupleRecord } from '../../../services/firebase/FirebaseCoupleService';

// Helper function to convert 24-hour time to 12-hour format
const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Helper: Firestore stores appointment dates as UTC midnight. Convert to local date.
const toLocalDate = (d: Date): Date =>
  new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

// Helper: team members may have enum roles ('system_admin') or display roles ('System Administrator')
const isProviderRole = (role: string) => {
  const r = role.toLowerCase().replace(/[\s()]/g, '_');
  return r.includes('provider') || r.includes('clinical_director') || r.includes('system_admin') || r.includes('system_administrator');
};

interface ScheduleTabProps {
  providerId: string;
  showAllProviders?: boolean;
  onStartSession?: (appointment: Appointment) => void;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({ providerId, showAllProviders = false, onStartSession }) => {
  const { roles: authRoles } = useAuth();
  const isAdminStaff = authRoles.includes('administrative_staff') && !authRoles.includes('provider') && !authRoles.includes('clinical_director') && !authRoles.includes('system_admin');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [preSelectedTime, setPreSelectedTime] = useState<string | null>(null);
  const [preSelectedDate, setPreSelectedDate] = useState<Date | null>(null);
  const [reschedulePatientId, setReschedulePatientId] = useState<string | null>(null);
  const [rescheduleAppointmentId, setRescheduleAppointmentId] = useState<string | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [pendingReschedule, setPendingReschedule] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [providerAvailability, setProviderAvailability] = useState<ProviderAvailability[]>([]);
  const [viewMode, setViewMode] = useState<'calendar' | 'timeblock'>('calendar');
  const [selectedDateForTimeBlock, setSelectedDateForTimeBlock] = useState(new Date());

  // Admin/staff: provider filter
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [teamProviders, setTeamProviders] = useState<TeamMember[]>([]);

  // Send no-show notification for an appointment (branded email + SMS, best-effort, once per appointment)
  const noShowEmailsSent = React.useRef<Set<string>>(new Set());
  const sendNoShowEmail = async (apt: Appointment) => {
    if (noShowEmailsSent.current.has(apt.id)) return;
    noShowEmailsSent.current.add(apt.id);
    if (!apt.patientId) return;
    try {
      const aptDate = new Date(apt.appointmentDate);
      const dateStr = new Date(aptDate.getUTCFullYear(), aptDate.getUTCMonth(), aptDate.getUTCDate())
        .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
      // Use branded notification API (email + SMS)
      NotificationTriggerService.getInstance().sendNoShow({
        patientId: apt.patientId,
        providerId: apt.providerId || providerId,
        appointmentDate: dateStr,
        startTime: apt.startTime ? formatTime12Hour(apt.startTime) : '',
        appointmentId: apt.id,
      }).catch(() => {});
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadAppointments();
    loadAvailability();
  }, [providerId, providerFilter, showAllProviders]);

  // Trigger 15-min "starting soon" reminders for appointments starting within 5-20 minutes
  const startingSoonSent = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    if (appointments.length === 0) return;
    const now = new Date();
    appointments.forEach(apt => {
      if (apt.status !== 'scheduled' && apt.status !== 'confirmed') return;
      if (startingSoonSent.current.has(apt.id)) return;
      if (!apt.patientId) return;
      const aptDate = new Date(apt.appointmentDate);
      const aptLocal = new Date(aptDate.getUTCFullYear(), aptDate.getUTCMonth(), aptDate.getUTCDate());
      if (apt.startTime) {
        const [h, m] = apt.startTime.split(':').map(Number);
        aptLocal.setHours(h, m, 0, 0);
      }
      const minsUntil = (aptLocal.getTime() - now.getTime()) / 60000;
      if (minsUntil >= 5 && minsUntil <= 20) {
        startingSoonSent.current.add(apt.id);
        fetch('/api/send-event-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'starting-soon',
            patientId: apt.patientId,
            providerId: apt.providerId || providerId,
            data: { startTime: apt.startTime ? formatTime12Hour(apt.startTime) : '', modality: apt.modality || 'in-person' },
          }),
        }).catch(() => {});
      }
    });
  }, [appointments, providerId]);

  // Load team members for provider filter dropdown (admin only)
  useEffect(() => {
    if (showAllProviders) {
      const service = TeamMemberService.getInstance();
      const members = service.getAllTeamMembers();
      setTeamProviders(members);

      // Subscribe to updates
      const listener = () => {
        setTeamProviders(service.getAllTeamMembers());
      };
      service.subscribe(listener);
      return () => service.unsubscribe(listener);
    }
  }, [showAllProviders]);

  const loadAppointments = async () => {
    try {
      // Admin staff with "all" filter loads ALL appointments; otherwise filter by selected provider
      const effectiveProviderId = showAllProviders && providerFilter === 'all'
        ? undefined
        : (showAllProviders ? providerFilter : providerId);

      console.log('ScheduleTab: loading appointments for providerId:', effectiveProviderId || 'ALL');
      const appts = await firebaseAppointmentService.getAppointments(
        effectiveProviderId ? { providerId: effectiveProviderId } : undefined
      );
      console.log('ScheduleTab: loaded', appts.length, 'appointments from Firestore', appts);

      // Auto-mark past scheduled/confirmed appointments as no-show + send email
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      for (const apt of appts) {
        if (apt.status !== 'scheduled' && apt.status !== 'confirmed') continue;
        const aptDate = new Date(apt.appointmentDate);
        const aptLocal = new Date(aptDate.getUTCFullYear(), aptDate.getUTCMonth(), aptDate.getUTCDate());
        let shouldMarkNoShow = false;
        if (aptLocal < today) {
          shouldMarkNoShow = true;
        } else if (aptLocal.getTime() === today.getTime() && apt.endTime) {
          const [eh, em] = apt.endTime.split(':').map(Number);
          if (now.getHours() > eh || (now.getHours() === eh && now.getMinutes() > em)) {
            shouldMarkNoShow = true;
          }
        }
        if (shouldMarkNoShow) {
          firebaseAppointmentService.updateAppointment(apt.id, { status: 'no-show' }).catch(() => {});
          apt.status = 'no-show';
          // Send no-show email (best-effort, non-blocking)
          sendNoShowEmail(apt);
        }
      }

      setAppointments(appts);
    } catch (error) {
      console.error('Failed to load appointments from Firestore:', error);
      // Firestore load failed — set empty
      setAppointments([]);
    }
  };

  const loadAvailability = async () => {
    // Always load own availability — admin needs it for Manage Availability modal
    const effectiveId = showAllProviders && providerFilter !== 'all'
      ? providerFilter
      : providerId;

    console.log('loadAvailability: effectiveId=', effectiveId, 'providerId=', providerId, 'providerFilter=', providerFilter, 'showAllProviders=', showAllProviders);

    if (!effectiveId) {
      console.warn('loadAvailability: no effectiveId, skipping');
      setProviderAvailability([]);
      return;
    }

    // Firestore is the source of truth for availability
    try {
      const firestoreAvail = await firebaseAppointmentService.getProviderAvailability(effectiveId);
      console.log('loadAvailability: got', firestoreAvail.length, 'records', firestoreAvail);
      setProviderAvailability(firestoreAvail);
    } catch (err) {
      console.error('Failed to load availability from Firestore:', err);
      setProviderAvailability([]);
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetails(true);
  };

  const [joinErrorModal, setJoinErrorModal] = useState<{ show: boolean; reason: string; minutesUntil?: number }>({
    show: false,
    reason: ''
  });

  const handleJoinVideoSession = async (appointment: Appointment) => {
    const canJoin = await firebaseAppointmentService.canJoinAppointment(appointment.id);

    if (canJoin.canJoin) {
      // Navigate to sessions tab with appointment data
      if (onStartSession) {
        onStartSession(appointment);
      }
    } else {
      setJoinErrorModal({
        show: true,
        reason: canJoin.reason || 'Cannot join at this time',
        minutesUntil: canJoin.minutesUntil
      });
    }
  };

  const handleStartInPersonSession = (appointment: Appointment) => {
    // Navigate to sessions tab with appointment data
    if (onStartSession) {
      onStartSession(appointment);
    }
  };

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(null);

  const handleCancelAppointment = (appointmentId: string) => {
    setAppointmentToCancel(appointmentId);
    setShowCancelModal(true);
  };

  const confirmCancelAppointment = async () => {
    if (appointmentToCancel) {
      try {
        await firebaseAppointmentService.cancelAppointment(appointmentToCancel);
      } catch (error) {
        console.error('Failed to cancel in Firestore:', error);
      }
      await loadAppointments();
      setShowAppointmentDetails(false);
      setShowCancelModal(false);
      setAppointmentToCancel(null);

      // Legacy reschedule cleanup
      setPendingReschedule(false);
    }
  };

  const getTodaysAppointments = (): Appointment[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return appointments.filter(appt => {
      const apptDate = toLocalDate(new Date(appt.appointmentDate));
      return apptDate >= today && apptDate < tomorrow && appt.status !== 'cancelled' && appt.status !== 'completed';
    }).sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const getUpcomingAppointments = (): Appointment[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return appointments.filter(appt => {
      const apptDate = toLocalDate(new Date(appt.appointmentDate));
      return apptDate >= today && (appt.status === 'scheduled' || appt.status === 'confirmed');
    }).sort((a, b) => {
      const dateCompare = toLocalDate(new Date(a.appointmentDate)).getTime() - toLocalDate(new Date(b.appointmentDate)).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    }).slice(0, 5);
  };

  const todaysAppointments = getTodaysAppointments();
  const upcomingAppointments = getUpcomingAppointments();

  return (
    <div style={{ padding: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0, color: '#f9fafb' }}>
              {showAllProviders ? 'All Providers — Schedule & Appointments' : 'Schedule & Appointments'}
            </h2>
            <button
              onClick={() => setShowHowItWorksModal(true)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '2px solid #6CA6C1',
                backgroundColor: 'transparent',
                color: '#6CA6C1',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#6CA6C1';
                e.currentTarget.style.color = '#1e293b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6CA6C1';
              }}
              title="How appointment scheduling works"
            >
              ?
            </button>
          </div>

          {/* View Toggle - Smaller, less prominent */}
          <div style={{
            display: 'flex',
            backgroundColor: '#1f2937',
            borderRadius: '4px',
            padding: '2px',
            gap: '2px',
            width: 'fit-content'
          }}>
            <button
              onClick={() => setViewMode('calendar')}
              style={{
                padding: '4px 12px',
                backgroundColor: viewMode === 'calendar' ? '#374151' : 'transparent',
                color: viewMode === 'calendar' ? '#f9fafb' : '#9ca3af',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: viewMode === 'calendar' ? '500' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              Calendar
            </button>
            <button
              onClick={() => setViewMode('timeblock')}
              style={{
                padding: '4px 12px',
                backgroundColor: viewMode === 'timeblock' ? '#374151' : 'transparent',
                color: viewMode === 'timeblock' ? '#f9fafb' : '#9ca3af',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: viewMode === 'timeblock' ? '500' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              Time Blocks
            </button>
          </div>

          {/* Provider Filter — admin/staff only */}
          {showAllProviders && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ color: '#9ca3af', fontSize: '13px', whiteSpace: 'nowrap' }}>Provider:</label>
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                style={{
                  backgroundColor: '#1f2937',
                  color: '#f9fafb',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  minWidth: '180px'
                }}
              >
                <option value="all">All Providers</option>
                {teamProviders.filter(m => isProviderRole(m.role)).map(member => (
                  <option key={member.authUid || member.id} value={member.authUid || member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowNewAppointmentModal(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '15px'
            }}
          >
            + New Appointment
          </button>
          {!isAdminStaff && (
            <button
              onClick={() => setShowAvailabilityModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: '#6CA6C1',
                color: '#1e293b',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '15px'
              }}
            >
              Manage Availability
            </button>
          )}
        </div>
      </div>

      {/* Today's Appointments */}
      {todaysAppointments.length > 0 && (
        <div style={{
          backgroundColor: '#1e293b',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          border: '2px solid #6CA6C1'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#f9fafb' }}>Today's Schedule</h3>

          {todaysAppointments.map(appt => (
            <div
              key={appt.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                backgroundColor: '#28527A',
                borderRadius: '6px',
                marginBottom: '8px',
                borderLeft: `4px solid ${appt.modality === 'video' ? '#3b82f6' : '#10b981'}`
              }}
            >
              <div>
                <div style={{ fontWeight: 'bold', color: '#f9fafb', marginBottom: '4px' }}>
                  {formatTime12Hour(appt.startTime)} - {formatTime12Hour(appt.endTime)} {appt.modality === 'video' ? '📹' : '👤'}
                </div>
                <div style={{ fontSize: '14px', color: '#d1d5db' }}>
                  {appt.patientName || 'Patient'} • {appt.sessionType}
                </div>
                {showAllProviders && appt.providerName && (
                  <div style={{ fontSize: '12px', color: '#DBA53A', marginTop: '2px' }}>
                    Dr. {appt.providerName}
                  </div>
                )}
                {appt.modality === 'video' && (
                  <div style={{ fontSize: '12px', color: '#6CA6C1', marginTop: '4px' }}>
                    Video Session
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                {appt.status !== 'completed' && appt.status !== 'cancelled' && (
                  appt.modality === 'video' ? (
                    <button
                      onClick={() => handleJoinVideoSession(appt)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}
                    >
                      Join Video
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStartInPersonSession(appt)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}
                    >
                      Start Session
                    </button>
                  )
                )}

                <button
                  onClick={() => handleAppointmentClick(appt)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#374151',
                    color: '#f9fafb',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming Appointments */}
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#f9fafb' }}>Upcoming Appointments</h3>

        {upcomingAppointments.length === 0 ? (
          <p style={{ color: '#d1d5db', fontSize: '14px' }}>No upcoming appointments</p>
        ) : (
          upcomingAppointments.map(appt => (
            <div
              key={appt.id}
              onClick={() => handleAppointmentClick(appt)}
              style={{
                padding: '12px',
                backgroundColor: '#28527A',
                borderRadius: '6px',
                marginBottom: '8px',
                cursor: 'pointer',
                borderLeft: `4px solid ${appt.modality === 'video' ? '#3b82f6' : '#10b981'}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontWeight: 'bold', color: '#f9fafb' }}>
                  {toLocalDate(new Date(appt.appointmentDate)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>
                  {formatTime12Hour(appt.startTime)}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: '#d1d5db' }}>
                {appt.patientName || 'Patient'} • {appt.modality === 'video' ? '📹 Video' : '👤 In-Person'}
              </div>
              {showAllProviders && appt.providerName && (
                <div style={{ fontSize: '12px', color: '#DBA53A', marginTop: '2px' }}>
                  Dr. {appt.providerName}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Calendar or Time Block View */}
      {viewMode === 'calendar' ? (
        <Calendar
          appointments={appointments}
          onAppointmentClick={handleAppointmentClick}
          onDateClick={(date) => {
            setSelectedDateForTimeBlock(date);
            setViewMode('timeblock');
          }}
          providerId={showAllProviders && providerFilter !== 'all' ? providerFilter : providerId}
          showProviderName={showAllProviders}
        />
      ) : (
        <div>
          {/* Date Navigation for Time Block View */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            backgroundColor: '#1e293b',
            padding: '12px',
            borderRadius: '8px'
          }}>
            <button
              onClick={() => {
                const newDate = new Date(selectedDateForTimeBlock);
                newDate.setDate(newDate.getDate() - 1);
                setSelectedDateForTimeBlock(newDate);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Previous Day
            </button>

            <button
              onClick={() => setSelectedDateForTimeBlock(new Date())}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6CA6C1',
                color: '#1e293b',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Today
            </button>

            <button
              onClick={() => {
                const newDate = new Date(selectedDateForTimeBlock);
                newDate.setDate(newDate.getDate() + 1);
                setSelectedDateForTimeBlock(newDate);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Next Day →
            </button>
          </div>

          <TimeBlockView
            selectedDate={selectedDateForTimeBlock}
            availability={providerAvailability}
            appointments={appointments}
            onSlotClick={(startTime, endTime) => {
              // Pre-fill appointment modal with selected time
              setPreSelectedTime(startTime);
              setPreSelectedDate(selectedDateForTimeBlock);
              setShowNewAppointmentModal(true);
            }}
            showPatientNames={true}
            providerId={providerId}
            useFirestore={true}
            onAvailabilityChange={() => {
              loadAvailability();
              loadAppointments();
            }}
          />
        </div>
      )}

      {/* Appointment Details Modal */}
      {showAppointmentDetails && selectedAppointment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1e293b',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #6CA6C1'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#f9fafb' }}>Appointment Details</h3>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Patient</div>
              <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 'bold' }}>
                {selectedAppointment.patientName || 'Patient'}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Date & Time</div>
              <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                {toLocalDate(new Date(selectedAppointment.appointmentDate)).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                {formatTime12Hour(selectedAppointment.startTime)} - {formatTime12Hour(selectedAppointment.endTime)}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Modality</div>
              <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                {selectedAppointment.modality === 'video' ? '📹 Video Session' : '👤 In-Person Session'}
              </div>
              {selectedAppointment.modality === 'video' && selectedAppointment.videoLink && (
                <div style={{ fontSize: '12px', color: '#6CA6C1', marginTop: '4px', wordBreak: 'break-all' }}>
                  {selectedAppointment.videoLink}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Session Type</div>
              <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                {selectedAppointment.sessionType}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Status</div>
              <div style={{
                display: 'inline-block',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                backgroundColor:
                  selectedAppointment.status === 'completed' ? '#10b981' :
                  selectedAppointment.status === 'in-progress' ? '#f59e0b' :
                  selectedAppointment.status === 'cancelled' ? '#ef4444' :
                  selectedAppointment.status === 'no-show' ? '#dc2626' :
                  '#6CA6C1',
                color: 'white'
              }}>
                {selectedAppointment.status.toUpperCase()}
              </div>
            </div>

            {selectedAppointment.notes && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Notes</div>
                <div style={{ color: '#f9fafb', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {selectedAppointment.notes}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              {selectedAppointment.status === 'scheduled' || selectedAppointment.status === 'confirmed' ? (
                <>
                  {selectedAppointment.modality === 'video' ? (
                    <button
                      onClick={() => handleJoinVideoSession(selectedAppointment)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Join Video Session
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStartInPersonSession(selectedAppointment)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Start Session
                    </button>
                  )}

                  <button
                    onClick={() => {
                      // Edit: open modal pre-filled with all appointment data for full editing
                      setRescheduleAppointmentId(selectedAppointment.id);
                      setReschedulePatientId(selectedAppointment.patientId);
                      setEditingAppointment(selectedAppointment);
                      setPreSelectedDate(toLocalDate(new Date(selectedAppointment.appointmentDate)));
                      setPreSelectedTime(selectedAppointment.startTime);
                      setShowAppointmentDetails(false);
                      setShowNewAppointmentModal(true);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#6CA6C1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => {
                      // Reschedule: move appointment to new date/time (updates existing, no cancel needed)
                      setRescheduleAppointmentId(selectedAppointment.id);
                      setReschedulePatientId(selectedAppointment.patientId);
                      setShowAppointmentDetails(false);
                      setShowNewAppointmentModal(true);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Reschedule
                  </button>

                  <button
                    onClick={() => handleCancelAppointment(selectedAppointment.id)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      await firebaseAppointmentService.updateAppointment(selectedAppointment.id, { status: 'no-show' });
                      sendNoShowEmail(selectedAppointment);
                      await loadAppointments();
                      setShowAppointmentDetails(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    No-Show
                  </button>
                </>
              ) : null}

              <button
                onClick={() => setShowAppointmentDetails(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Appointment Modal */}
      {showNewAppointmentModal && (
        <NewAppointmentModal
          providerId={providerId}
          initialTime={preSelectedTime}
          initialDate={preSelectedDate}
          initialPatientId={reschedulePatientId}
          rescheduleAppointmentId={rescheduleAppointmentId}
          editingAppointment={editingAppointment}
          onClose={() => {
            setShowNewAppointmentModal(false);
            setPreSelectedTime(null);
            setPreSelectedDate(null);
            setReschedulePatientId(null);
            setRescheduleAppointmentId(null);
            setEditingAppointment(null);
            loadAppointments();
          }}
        />
      )}

      {/* Availability Modal */}
      {showAvailabilityModal && (
        <AvailabilityManager
          providerId={providerId}
          availability={providerAvailability}
          onClose={() => {
            setShowAvailabilityModal(false);
            loadAvailability();
          }}
        />
      )}

      {/* Join Error Modal */}
      {joinErrorModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            border: '2px solid #ef4444',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Warning Icon */}
            <div style={{
              fontSize: '48px',
              textAlign: 'center',
              marginBottom: '16px'
            }}>
              ⚠️
            </div>

            <h3 style={{
              margin: '0 0 16px 0',
              color: '#f9fafb',
              textAlign: 'center',
              fontSize: '20px'
            }}>
              Cannot Join Session
            </h3>

            <p style={{
              color: '#d1d5db',
              fontSize: '14px',
              textAlign: 'center',
              marginBottom: '8px'
            }}>
              {joinErrorModal.reason}
            </p>

            {joinErrorModal.minutesUntil !== undefined && (
              <p style={{
                color: '#6CA6C1',
                fontSize: '14px',
                textAlign: 'center',
                marginBottom: '16px',
                fontWeight: 'bold'
              }}>
                You can join in {joinErrorModal.minutesUntil} minute{joinErrorModal.minutesUntil !== 1 ? 's' : ''}
              </p>
            )}

            <button
              onClick={() => setJoinErrorModal({ show: false, reason: '' })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginTop: '16px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Cancel Appointment Confirmation Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            border: '2px solid #ef4444',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: '#f9fafb',
              textAlign: 'center',
              fontSize: '18px'
            }}>
              Cancel Appointment?
            </h3>

            <p style={{
              color: '#d1d5db',
              fontSize: '14px',
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              Are you sure you want to cancel this appointment?
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={confirmCancelAppointment}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#ef4444',
                  color: '#f9fafb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Cancel Appointment
              </button>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setAppointmentToCancel(null);
                  setPendingReschedule(false);
                  setReschedulePatientId(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#6CA6C1',
                  color: '#1e293b',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Keep Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Modal */}
      {showHowItWorksModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '85vh',
            overflow: 'auto',
            border: '2px solid #6CA6C1',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ margin: 0, color: '#f9fafb', fontSize: '24px' }}>
                How Appointment Scheduling Works
              </h2>
              <button
                onClick={() => setShowHowItWorksModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ color: '#f9fafb', lineHeight: '1.7' }}>
              <section style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#6CA6C1', fontSize: '18px', marginBottom: '12px' }}>
                  Setting Your Availability
                </h3>
                <p style={{ marginBottom: '12px' }}>
                  When you set your available hours and slot duration, the system creates time slots that clients can book.
                </p>
              </section>

              <section style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#6CA6C1', fontSize: '18px', marginBottom: '12px' }}>
                  Appointment Durations
                </h3>
                <div style={{ backgroundColor: '#374151', padding: '16px', borderRadius: '8px', marginBottom: '12px' }}>
                  <div style={{ marginBottom: '8px', color: '#f9fafb' }}><strong>• 30 minutes</strong> → Check-ins, Quick follow-ups</div>
                  <div style={{ marginBottom: '8px', color: '#f9fafb' }}><strong>• 45 minutes</strong> → Shorter therapy sessions</div>
                  <div style={{ marginBottom: '8px', color: '#f9fafb' }}><strong>• 60 minutes</strong> → Standard sessions, Initial consultations</div>
                  <div style={{ color: '#f9fafb' }}><strong>• 90 minutes</strong> → Comprehensive assessments, Evaluations</div>
                </div>
              </section>

              <section style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#6CA6C1', fontSize: '18px', marginBottom: '12px' }}>
                  Client Booking Process
                </h3>
                <ol style={{ paddingLeft: '20px', margin: 0, color: '#f9fafb' }}>
                  <li style={{ marginBottom: '8px' }}>Client selects the type of appointment they need</li>
                  <li style={{ marginBottom: '8px' }}>System shows only time slots that fit that duration</li>
                  <li>Client books an available slot</li>
                </ol>
              </section>

              <section style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#6CA6C1', fontSize: '18px', marginBottom: '12px' }}>
                  Example
                </h3>
                <div style={{ backgroundColor: '#374151', padding: '16px', borderRadius: '8px' }}>
                  <p style={{ marginBottom: '12px', fontWeight: 'bold', color: '#f9fafb' }}>
                    If you set: 9:00 AM - 12:00 PM with 60-minute slots
                  </p>
                  <p style={{ marginBottom: '12px', color: '#f9fafb' }}>
                    System creates: 9:00 AM, 10:00 AM, 11:00 AM slots
                  </p>
                  <p style={{ marginBottom: '12px', color: '#f9fafb' }}>
                    <strong style={{ color: '#10b981' }}>Clients needing 30-min check-ins can book:</strong><br />
                    9:00, 9:30, 10:00, 10:30, 11:00, 11:30
                  </p>
                  <p style={{ color: '#f9fafb' }}>
                    <strong style={{ color: '#3b82f6' }}>Clients needing 60-min sessions can book:</strong><br />
                    9:00, 10:00, 11:00
                  </p>
                </div>
              </section>
            </div>

            <button
              onClick={() => setShowHowItWorksModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#6CA6C1',
                color: '#1e293b',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                marginTop: '8px'
              }}
            >
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// New Appointment Modal Component
interface NewAppointmentModalProps {
  providerId: string;
  initialTime?: string | null;
  initialDate?: Date | null;
  initialPatientId?: string | null;
  rescheduleAppointmentId?: string | null;
  editingAppointment?: Appointment | null;
  onClose: () => void;
}

const NewAppointmentModal: React.FC<NewAppointmentModalProps> = ({ providerId, initialTime, initialDate, initialPatientId, rescheduleAppointmentId, editingAppointment, onClose }) => {
  const toast = useToast();
  const { user, roles } = useAuth();
  const [patients, setPatients] = React.useState<Patient[]>([]);
  const [availableSlots, setAvailableSlots] = React.useState<Array<{ startTime: string; endTime: string; available: boolean }>>([]);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [useCustomTime, setUseCustomTime] = React.useState(false);
  const [teamProvidersList, setTeamProvidersList] = React.useState<TeamMember[]>([]);
  const [selectedProviderId, setSelectedProviderId] = React.useState(providerId);

  // Build RoleUser for permission checks
  const roleUser: RoleUser | null = user && roles.length > 0 ? {
    uid: user.uid, roles, primaryRole: roles[0]
  } : null;

  const isAdminOrDirector = hasAnyRole(roleUser, ['system_admin', 'clinical_director', 'administrative_staff']);

  // Load team members for provider selector (admin/director/staff only)
  React.useEffect(() => {
    if (isAdminOrDirector) {
      const service = TeamMemberService.getInstance();
      const updateProviders = () => {
        const members = service.getAllTeamMembers();
        setTeamProvidersList(members.filter(m => isProviderRole(m.role)));
      };
      updateProviders();
      service.subscribe(updateProviders);
      return () => service.unsubscribe(updateProviders);
    }
  }, [isAdminOrDirector]);

  React.useEffect(() => {
    // Admin staff, system admin, and clinical director see all practice patients
    if (isAdminOrDirector) {
      FirebasePatientService.getAllPracticePatients().then(setPatients);
    } else if (providerId) {
      FirebasePatientService.getAllPatients(providerId).then(setPatients);
    }
  }, [providerId, roleUser?.uid]);

  const [formData, setFormData] = useState({
    patientId: initialPatientId || '',
    appointmentDate: initialDate
      ? initialDate.toISOString().split('T')[0]
      : editingAppointment
      ? toLocalDate(new Date(editingAppointment.appointmentDate)).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    startTime: initialTime || editingAppointment?.startTime || '',
    duration: editingAppointment?.duration || 60,
    modality: (editingAppointment?.modality || 'video') as AppointmentModality,
    sessionType: editingAppointment?.sessionType || 'therapy' as string,
    appointmentReason: editingAppointment?.appointmentReason || '',
    location: (typeof editingAppointment?.location === 'string' ? editingAppointment.location : '') || ''
  });

  // Couples support
  const [providerCouples, setProviderCouples] = React.useState<CoupleRecord[]>([]);
  const [selectedCoupleId, setSelectedCoupleId] = React.useState('');

  // Load couples based on loaded patients (not provider) so transferred couples show
  React.useEffect(() => {
    if (patients.length === 0) return;
    const patientIds = patients.map(p => p.id);
    const unsub = FirebaseCoupleService.subscribeToCouplesByPatients(patientIds, (couples) => {
      setProviderCouples(couples.filter(c => c.status === 'active'));
    });
    return unsub;
  }, [patients]);

  // When session type is couples or initial-consultation, auto-fill couple if patient is in one
  const isCouplesEligible = formData.sessionType === 'couples' || formData.sessionType === 'initial-consultation';
  React.useEffect(() => {
    if (isCouplesEligible && formData.patientId) {
      const couple = providerCouples.find(
        c => c.patient1Id === formData.patientId || c.patient2Id === formData.patientId
      );
      if (couple) {
        setSelectedCoupleId(couple.id);
      } else {
        setSelectedCoupleId('');
      }
    } else {
      setSelectedCoupleId('');
    }
  }, [isCouplesEligible, formData.patientId, providerCouples]);

  // Ensure initialPatientId is applied once patient list loads (fixes reschedule pre-fill)
  React.useEffect(() => {
    if (initialPatientId && patients.length > 0) {
      setFormData(prev => ({ ...prev, patientId: initialPatientId }));
    }
  }, [patients, initialPatientId]);

  // Fetch available slots when date or selected provider changes
  const effectiveSlotProviderId = isAdminOrDirector ? selectedProviderId : providerId;
  React.useEffect(() => {
    const fetchSlots = async () => {
      if (!formData.appointmentDate || !effectiveSlotProviderId) return;
      setLoadingSlots(true);
      try {
        const date = new Date(formData.appointmentDate + 'T00:00:00');
        const slots = await firebaseAppointmentService.getAvailableSlots(effectiveSlotProviderId, date);
        setAvailableSlots(slots);
        // If initialTime matches a slot, keep it; otherwise clear
        if (initialTime && slots.some(s => s.startTime === initialTime && s.available)) {
          setFormData(prev => ({ ...prev, startTime: initialTime }));
        } else if (!slots.some(s => s.startTime === formData.startTime && s.available)) {
          setFormData(prev => ({ ...prev, startTime: '' }));
        }
      } catch (err) {
        console.error('Failed to load available slots:', err);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [formData.appointmentDate, effectiveSlotProviderId]);

  const handleSubmit = async () => {
    if (!formData.patientId) {
      toast.warning('No Patient Selected', 'Please select a patient.');
      return;
    }

    const patient = patients.find(p => p.id === formData.patientId);
    if (!patient) return;

    const effectiveProviderId = isAdminOrDirector ? selectedProviderId : providerId;

    const appointment: Record<string, any> = {
      patientId: formData.patientId,
      patientName: `${patient.firstName} ${patient.lastName}`,
      providerId: effectiveProviderId,
      createdBy: providerId,
      appointmentDate: new Date(formData.appointmentDate + 'T00:00:00'),
      startTime: formData.startTime,
      endTime: calculateEndTime(formData.startTime, formData.duration),
      duration: formData.duration,
      modality: formData.modality,
      sessionType: formData.sessionType,
      status: 'scheduled',
    };

    // Add couple data if couples or couples initial consultation
    if (isCouplesEligible && selectedCoupleId) {
      const couple = providerCouples.find(c => c.id === selectedCoupleId);
      if (couple) {
        appointment.coupleId = couple.id;
        // patient2 is the other partner
        const isPatient1 = formData.patientId === couple.patient1Id;
        appointment.patient2Id = isPatient1 ? couple.patient2Id : couple.patient1Id;
        appointment.patient2Name = isPatient1 ? couple.patient2Name : couple.patient1Name;
      }
    }

    // Only add optional fields if they have values — Firestore rejects undefined
    if (formData.appointmentReason) {
      appointment.appointmentReason = formData.appointmentReason;
    }
    if (formData.modality === 'in-person') {
      appointment.location = formData.location || 'Office';
    }

    try {
      if (rescheduleAppointmentId) {
        // Update existing appointment (edit or reschedule)
        const updateData: Record<string, any> = {
          appointmentDate: appointment.appointmentDate,
          startTime: appointment.startTime,
          endTime: appointment.endTime,
          duration: appointment.duration,
          modality: appointment.modality,
          sessionType: appointment.sessionType,
          status: 'scheduled',
        };
        if (appointment.appointmentReason) updateData.appointmentReason = appointment.appointmentReason;
        if (appointment.location) updateData.location = appointment.location;
        // Update couple data if changed
        if (isCouplesEligible && selectedCoupleId) {
          const couple = providerCouples.find(c => c.id === selectedCoupleId);
          if (couple) {
            updateData.coupleId = couple.id;
            const isPatient1 = formData.patientId === couple.patient1Id;
            updateData.patient2Id = isPatient1 ? couple.patient2Id : couple.patient1Id;
            updateData.patient2Name = isPatient1 ? couple.patient2Name : couple.patient1Name;
          }
        }
        await firebaseAppointmentService.updateAppointment(rescheduleAppointmentId, updateData);
        toast.success('Appointment Updated', 'Appointment has been updated.');
      } else {
        await firebaseAppointmentService.createAppointment(appointment as any);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save appointment:', error);
      toast.error('Error', 'Failed to save appointment. Please try again.');
    }
  };

  const calculateEndTime = (startTime: string, duration: number): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '2px solid #6CA6C1'
      }}>
        <h3 style={{ margin: '0 0 24px 0', color: '#f9fafb' }}>{editingAppointment ? 'Edit Appointment' : rescheduleAppointmentId ? 'Reschedule Appointment' : 'Schedule New Appointment'}</h3>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
            Patient *
          </label>
          <select
            value={formData.patientId}
            onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: '1px solid #4b5563',
              borderRadius: '6px'
            }}
          >
            <option value="">-- Select Patient --</option>
            {patients.map(patient => (
              <option key={patient.id} value={patient.id}>
                {patient.firstName} {patient.lastName} - {patient.email}
              </option>
            ))}
          </select>
        </div>

        {/* Provider selector — visible to admin/director/staff */}
        {isAdminOrDirector && teamProvidersList.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
              Provider *
            </label>
            <select
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '6px'
              }}
            >
              {teamProvidersList.map(member => (
                <option key={member.authUid || member.id} value={member.authUid || member.id}>
                  {member.name} — {member.role.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
            Date *
          </label>
          <input
            type="date"
            value={formData.appointmentDate}
            onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value, startTime: '' })}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: '1px solid #4b5563',
              borderRadius: '6px'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
            Available Time Slots *
          </label>
          {!useCustomTime && (
            <>
              {loadingSlots ? (
                <div style={{ padding: '12px', color: '#d1d5db', fontSize: '14px' }}>Loading available slots...</div>
              ) : availableSlots.length === 0 ? (
                <div style={{ padding: '12px', color: '#f59e0b', fontSize: '14px', backgroundColor: '#374151', borderRadius: '6px' }}>
                  No availability set for this date. Use custom time below.
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {availableSlots.map(slot => {
                    const isSelected = formData.startTime === slot.startTime;
                    const isBooked = !slot.available;
                    const [h, m] = slot.startTime.split(':').map(Number);
                    const period = h >= 12 ? 'PM' : 'AM';
                    const h12 = h % 12 || 12;
                    const label = `${h12}:${String(m).padStart(2, '0')} ${period}`;
                    return (
                      <button
                        key={slot.startTime}
                        type="button"
                        disabled={isBooked}
                        onClick={() => setFormData({ ...formData, startTime: slot.startTime })}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '6px',
                          border: isSelected ? '2px solid #6CA6C1' : '1px solid #4b5563',
                          backgroundColor: isBooked ? '#1f2937' : isSelected ? '#28527A' : '#374151',
                          color: isBooked ? '#4b5563' : isSelected ? '#f9fafb' : '#d1d5db',
                          cursor: isBooked ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          fontWeight: isSelected ? 'bold' : 'normal',
                          textDecoration: isBooked ? 'line-through' : 'none',
                          transition: 'all 0.15s'
                        }}
                        title={isBooked ? 'Already booked' : `Select ${label}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {useCustomTime && (
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          )}

          <button
            type="button"
            onClick={() => {
              setUseCustomTime(!useCustomTime);
              if (!useCustomTime) setFormData({ ...formData, startTime: '' });
            }}
            style={{
              marginTop: '8px',
              padding: '6px 12px',
              backgroundColor: 'transparent',
              color: '#6CA6C1',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            {useCustomTime ? 'Use Available Slots' : 'Custom Time'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
              Duration *
            </label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '6px'
              }}
            >
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
              <option value={90}>90 minutes</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
              Session Type *
            </label>
            <select
              value={formData.sessionType}
              onChange={(e) => setFormData({ ...formData, sessionType: e.target.value as any })}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '6px'
              }}
            >
              <option value="initial-consultation">Initial Consultation</option>
              <option value="follow-up">Follow-up</option>
              <option value="therapy">Therapy</option>
              <option value="couples">Couples Session</option>
              <option value="assessment">Assessment</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Couple Selector — shown for couples or initial-consultation */}
        {isCouplesEligible && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
              {formData.sessionType === 'couples' ? 'Couple *' : 'Include Partner? (optional for couples)'}
            </label>
            {providerCouples.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
                No active couples found. To schedule a couples session, link patients in the Clients tab first.
              </p>
            ) : (
              <select
                value={selectedCoupleId}
                onChange={(e) => {
                  const coupleId = e.target.value;
                  setSelectedCoupleId(coupleId);
                  // Auto-fill patient if not already selected
                  if (coupleId) {
                    const couple = providerCouples.find(c => c.id === coupleId);
                    if (couple && !formData.patientId) {
                      setFormData(prev => ({ ...prev, patientId: couple.patient1Id }));
                    }
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '6px'
                }}
              >
                <option value="">-- Select Couple --</option>
                {providerCouples.map(couple => (
                  <option key={couple.id} value={couple.id}>
                    {couple.patient1Name} & {couple.patient2Name}
                    {couple.coupleLabel ? ` (${couple.coupleLabel})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', color: '#e5e7eb' }}>
            How will this appointment take place? *
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* Video Option */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, modality: 'video' })}
              style={{
                padding: '16px',
                backgroundColor: formData.modality === 'video' ? '#3b82f6' : '#374151',
                color: '#f9fafb',
                border: formData.modality === 'video' ? '2px solid #60a5fa' : '2px solid #4b5563',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s',
                fontSize: '14px',
                fontWeight: formData.modality === 'video' ? 'bold' : 'normal',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (formData.modality !== 'video') {
                  e.currentTarget.style.backgroundColor = '#4b5563';
                }
              }}
              onMouseLeave={(e) => {
                if (formData.modality !== 'video') {
                  e.currentTarget.style.backgroundColor = '#374151';
                }
              }}
            >
              <div style={{ fontSize: '24px', marginBottom: '6px' }}>📹</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '2px' }}>VIDEO CALL</div>
              <div style={{ fontSize: '11px', color: '#d1d5db' }}>Join from anywhere</div>
              {formData.modality === 'video' && (
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  backgroundColor: '#10b981',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>
                  ✓
                </div>
              )}
            </button>

            {/* In-Person Option */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, modality: 'in-person' })}
              style={{
                padding: '16px',
                backgroundColor: formData.modality === 'in-person' ? '#10b981' : '#374151',
                color: '#f9fafb',
                border: formData.modality === 'in-person' ? '2px solid #34d399' : '2px solid #4b5563',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s',
                fontSize: '14px',
                fontWeight: formData.modality === 'in-person' ? 'bold' : 'normal',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (formData.modality !== 'in-person') {
                  e.currentTarget.style.backgroundColor = '#4b5563';
                }
              }}
              onMouseLeave={(e) => {
                if (formData.modality !== 'in-person') {
                  e.currentTarget.style.backgroundColor = '#374151';
                }
              }}
            >
              <div style={{ fontSize: '24px', marginBottom: '6px' }}>🏥</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '2px' }}>IN-PERSON</div>
              <div style={{ fontSize: '11px', color: '#d1d5db' }}>Visit our office</div>
              {formData.modality === 'in-person' && (
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>
                  ✓
                </div>
              )}
            </button>
          </div>
        </div>

        {formData.modality === 'in-person' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Office, Room 101, etc."
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '6px'
              }}
            />
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <label style={{ fontSize: '14px', color: '#e5e7eb' }}>
              Reason for Visit
            </label>
            <VoiceInputButton onResult={text => setFormData(prev => ({ ...prev, appointmentReason: prev.appointmentReason + ' ' + text }))} />
          </div>
          <textarea
            value={formData.appointmentReason}
            onChange={(e) => setFormData({ ...formData, appointmentReason: e.target.value })}
            placeholder="Brief description of the appointment purpose..."
            rows={3}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleSubmit}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {editingAppointment ? 'Save Changes' : rescheduleAppointmentId ? 'Move Appointment' : 'Schedule Appointment'}
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Availability Manager Component
interface AvailabilityManagerProps {
  providerId: string;
  availability: ProviderAvailability[];
  onClose: () => void;
}

const ALL_APPOINTMENT_TYPES: { key: AppointmentTypeKey; label: string; duration: string; durationMin: number }[] = [
  { key: 'initial-consultation', label: 'Initial Consultation', duration: '90 min', durationMin: 90 },
  { key: 'therapy', label: 'Individual Therapy', duration: '50 min', durationMin: 50 },
  { key: 'check-in', label: 'Check-in / Follow-up', duration: '30 min', durationMin: 30 },
  { key: 'couples-initial', label: 'Couples Initial', duration: '90 min', durationMin: 90 },
  { key: 'couples', label: 'Couples Session', duration: '50 min', durationMin: 50 },
];

interface BlockFormData {
  startTime: string;
  endTime: string;
  slotDuration: number;
  modalities: AppointmentModality[];
  appointmentTypes: AppointmentTypeKey[];
}

const emptyBlock = (): BlockFormData => ({
  startTime: '09:00',
  endTime: '12:00',
  slotDuration: 60,
  modalities: ['video', 'in-person'],
  appointmentTypes: ALL_APPOINTMENT_TYPES.map(t => t.key),
});

const AvailabilityManager: React.FC<AvailabilityManagerProps> = ({ providerId, availability, onClose }) => {
  const [editingDay, setEditingDay] = useState<number | null>(null);
  // Multi-block: array of blocks per day being edited
  const [dayBlocks, setDayBlocks] = useState<BlockFormData[]>([emptyBlock()]);
  const [showPreview, setShowPreview] = useState(false);

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const getAvailabilityForDay = (dayOfWeek: number): ProviderAvailability | undefined => {
    return availability.find(a => a.dayOfWeek === dayOfWeek && a.isActive);
  };

  const getBlocksForDay = (dayOfWeek: number): BlockFormData[] => {
    const avail = getAvailabilityForDay(dayOfWeek);
    if (!avail) return [];
    if (avail.blocks && avail.blocks.length > 0) {
      return avail.blocks.map(b => ({
        startTime: b.startTime,
        endTime: b.endTime,
        slotDuration: b.slotDuration,
        modalities: [...b.modalities],
        appointmentTypes: [...b.appointmentTypes],
      }));
    }
    // Legacy: single block from flat fields
    return [{
      startTime: avail.startTime,
      endTime: avail.endTime,
      slotDuration: avail.slotDuration,
      modalities: [...avail.modalities],
      appointmentTypes: avail.appointmentTypes?.length ? [...avail.appointmentTypes] : ALL_APPOINTMENT_TYPES.map(t => t.key),
    }];
  };

  const handleStartEditing = (dayIndex: number) => {
    const existing = getBlocksForDay(dayIndex);
    setDayBlocks(existing.length > 0 ? existing : [emptyBlock()]);
    setEditingDay(dayIndex);
  };

  const addBlock = () => {
    // Default new block starts after last block ends
    const lastBlock = dayBlocks[dayBlocks.length - 1];
    const newBlock = emptyBlock();
    if (lastBlock) {
      newBlock.startTime = lastBlock.endTime;
      // Add 2 hours
      const [h, m] = lastBlock.endTime.split(':').map(Number);
      const endH = Math.min(h + 2, 21);
      newBlock.endTime = `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    setDayBlocks([...dayBlocks, newBlock]);
  };

  const removeBlock = (idx: number) => {
    setDayBlocks(dayBlocks.filter((_, i) => i !== idx));
  };

  const updateBlock = (idx: number, updates: Partial<BlockFormData>) => {
    setDayBlocks(dayBlocks.map((b, i) => i === idx ? { ...b, ...updates } : b));
  };

  const handleSaveAvailability = async (dayOfWeek: number) => {
    // Build the first block's flat fields for backward compat
    const first = dayBlocks[0] || emptyBlock();
    const availData = {
      providerId,
      dayOfWeek,
      startTime: first.startTime,
      endTime: dayBlocks[dayBlocks.length - 1]?.endTime || first.endTime,
      slotDuration: first.slotDuration,
      modalities: first.modalities,
      appointmentTypes: first.appointmentTypes,
      blocks: dayBlocks.map(b => ({
        startTime: b.startTime,
        endTime: b.endTime,
        slotDuration: b.slotDuration,
        modalities: b.modalities,
        appointmentTypes: b.appointmentTypes,
      })),
      isActive: true,
    };

    try {
      await firebaseAppointmentService.setProviderAvailability(availData);
    } catch (err) {
      console.error('Failed to save availability to Firestore:', err);
    }

    setEditingDay(null);
    onClose();
  };

  const handleRemoveAvailability = async (dayOfWeek: number) => {
    try {
      await firebaseAppointmentService.setProviderAvailability({
        providerId,
        dayOfWeek,
        startTime: '09:00',
        endTime: '17:00',
        slotDuration: 60,
        modalities: ['video', 'in-person'],
        isActive: false,
      });
    } catch (err) {
      console.error('Failed to remove availability from Firestore:', err);
    }

    setEditingDay(null);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      overflow: 'auto'
    }}>
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '700px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '2px solid #6CA6C1'
      }}>
        <h3 style={{ margin: '0 0 24px 0', color: '#f9fafb' }}>Manage Weekly Availability</h3>

        {weekDays.map((day, index) => {
          const dayAvailability = getAvailabilityForDay(index);
          const existingBlocks = getBlocksForDay(index);
          const isEditing = editingDay === index;

          return (
            <div key={index} style={{ padding: '16px', backgroundColor: '#28527A', borderRadius: '8px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#f9fafb', marginBottom: '4px', fontSize: '15px' }}>{day}</div>
                  {existingBlocks.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {existingBlocks.map((block, bIdx) => (
                        <div key={bIdx} style={{ fontSize: '13px', color: '#d1d5db', padding: '6px 10px', backgroundColor: '#1e3a5f', borderRadius: '6px', border: '1px solid #374151' }}>
                          <div style={{ fontWeight: '600', color: '#93c5fd' }}>
                            {formatTime12Hour(block.startTime)} - {formatTime12Hour(block.endTime)} • {block.slotDuration} min slots
                          </div>
                          <div style={{ marginTop: '2px', fontSize: '11px' }}>
                            {block.modalities.includes('video') && '📹 Video '}
                            {block.modalities.includes('in-person') && '👤 In-Person'}
                          </div>
                          <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                            {block.appointmentTypes.map(typeKey => {
                              const t = ALL_APPOINTMENT_TYPES.find(at => at.key === typeKey);
                              return t ? (
                                <span key={typeKey} style={{ fontSize: '10px', backgroundColor: '#28527A', padding: '2px 6px', borderRadius: '3px', color: '#93c5fd', border: '1px solid #3b82f6' }}>
                                  {t.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Not available</div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginLeft: '12px', flexShrink: 0 }}>
                  {dayAvailability && !isEditing && (
                    <button onClick={() => handleRemoveAvailability(index)} style={{ padding: '6px 14px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => isEditing ? setEditingDay(null) : handleStartEditing(index)}
                    style={{ padding: '6px 14px', backgroundColor: '#6CA6C1', color: '#1e293b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                  >
                    {isEditing ? 'Cancel' : dayAvailability ? 'Edit' : 'Set Available'}
                  </button>
                </div>
              </div>

              {/* Multi-block editor */}
              {isEditing && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #374151' }}>
                  {dayBlocks.map((block, bIdx) => (
                    <div key={bIdx} style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#16325B', borderRadius: '8px', border: '1px solid #374151' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ color: '#D4A017', fontWeight: '600', fontSize: '13px' }}>Block {bIdx + 1}</span>
                        {dayBlocks.length > 1 && (
                          <button onClick={() => removeBlock(bIdx)} style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer' }}>
                            Remove Block
                          </button>
                        )}
                      </div>

                      {/* Time row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px', color: '#9ca3af' }}>Start</label>
                          <input type="time" value={block.startTime} onChange={(e) => updateBlock(bIdx, { startTime: e.target.value })} style={{ width: '100%', padding: '6px', backgroundColor: '#374151', color: '#f9fafb', border: '1px solid #4b5563', borderRadius: '4px', fontSize: '13px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px', color: '#9ca3af' }}>End</label>
                          <input type="time" value={block.endTime} onChange={(e) => updateBlock(bIdx, { endTime: e.target.value })} style={{ width: '100%', padding: '6px', backgroundColor: '#374151', color: '#f9fafb', border: '1px solid #4b5563', borderRadius: '4px', fontSize: '13px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px', color: '#9ca3af' }}>Slot Duration</label>
                          <select value={block.slotDuration} onChange={(e) => updateBlock(bIdx, { slotDuration: Number(e.target.value) })} style={{ width: '100%', padding: '6px', backgroundColor: '#374151', color: '#f9fafb', border: '1px solid #4b5563', borderRadius: '4px', fontSize: '13px' }}>
                            <option value={25}>25 min</option>
                            <option value={30}>30 min</option>
                            <option value={45}>45 min</option>
                            <option value={50}>50 min</option>
                            <option value={60}>60 min</option>
                            <option value={90}>90 min</option>
                          </select>
                        </div>
                      </div>

                      {/* Modalities */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f9fafb', cursor: 'pointer', fontSize: '12px' }}>
                          <input type="checkbox" checked={block.modalities.includes('video')} onChange={(e) => { const m = e.target.checked ? [...block.modalities, 'video' as AppointmentModality] : block.modalities.filter(x => x !== 'video'); updateBlock(bIdx, { modalities: m }); }} />
                          📹 Video
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f9fafb', cursor: 'pointer', fontSize: '12px' }}>
                          <input type="checkbox" checked={block.modalities.includes('in-person')} onChange={(e) => { const m = e.target.checked ? [...block.modalities, 'in-person' as AppointmentModality] : block.modalities.filter(x => x !== 'in-person'); updateBlock(bIdx, { modalities: m }); }} />
                          👤 In-Person
                        </label>
                      </div>

                      {/* Appointment types */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {ALL_APPOINTMENT_TYPES.map(type => {
                          const checked = block.appointmentTypes.includes(type.key);
                          return (
                            <label key={type.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: checked ? '#f9fafb' : '#6b7280', cursor: 'pointer', fontSize: '12px', padding: '4px 8px', borderRadius: '4px', backgroundColor: checked ? '#1e3a5f' : '#374151', border: `1px solid ${checked ? '#3b82f6' : '#4b5563'}` }}>
                              <input type="checkbox" checked={checked} onChange={(e) => { const t = e.target.checked ? [...block.appointmentTypes, type.key] : block.appointmentTypes.filter(x => x !== type.key); updateBlock(bIdx, { appointmentTypes: t }); }} style={{ accentColor: '#3b82f6' }} />
                              {type.label} <span style={{ color: '#9ca3af', fontSize: '10px' }}>({type.duration})</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Add Block button */}
                  <button onClick={addBlock} style={{ padding: '8px 16px', backgroundColor: '#374151', color: '#93c5fd', border: '1px dashed #6CA6C1', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', width: '100%', marginBottom: '12px' }}>
                    + Add Time Block
                  </button>

                  {/* Save */}
                  <button
                    onClick={() => handleSaveAvailability(index)}
                    disabled={dayBlocks.length === 0 || dayBlocks.some(b => b.modalities.length === 0 || b.appointmentTypes.length === 0)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: dayBlocks.some(b => b.modalities.length === 0 || b.appointmentTypes.length === 0) ? '#374151' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: dayBlocks.some(b => b.modalities.length === 0 || b.appointmentTypes.length === 0) ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      width: '100%',
                    }}
                  >
                    Save Availability
                  </button>
                </div>
              )}
            </div>
          );
        })}

        <button
          onClick={onClose}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: '#374151',
            color: '#f9fafb',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ScheduleTab;
